import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, RefreshCw, ChevronRight } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Note } from "@/providers/note-provider";
import React from "react";

interface NoteCardProps {
  note: Note;
  onEdit: (note: Note) => void;
  onNavigate: (note: Note) => void;
  onDelete: (note: Note) => Promise<void>;
}

export const NoteCard = ({
  note,
  onEdit,
  onNavigate,
  onDelete,
}: NoteCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: note._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [isDeleting, setIsDeleting] = React.useState(false);
  const hasChildren = note.children && note.children.length > 0;

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setIsDeleting(true);
      await onDelete(note);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onNavigate(note);
  };

  const handleEdit = () => {
    onEdit(note);
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="w-full hover:shadow-md transition-shadow">
        <CardContent className="p-0">
          <div className="flex items-stretch">
            <div
              {...attributes}
              {...listeners}
              className="flex-1 p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={handleEdit}
            >
              <div className="w-full font-semibold mb-1 line-clamp-2">
                {note.title}
              </div>
              {note.description && (
                <div className="w-full text-sm text-muted-foreground line-clamp-2">
                  {note.description}
                </div>
              )}
            </div>

            <div className="flex items-center border-l">
              <Button
                variant="ghost"
                size="icon"
                className="h-full rounded-none px-3 hover:bg-muted/50"
                onClick={handleNavigate}
              >
                <div className="flex items-center gap-1">
                  {hasChildren && (
                    <span className="text-xs text-muted-foreground font-medium">
                      {note.children?.length}
                    </span>
                  )}
                  <ChevronRight
                    className={`h-4 w-4 ${hasChildren ? "text-foreground" : "text-muted-foreground/50"}`}
                  />
                </div>
              </Button>
            </div>
          </div>

          <div className="flex justify-end border-t px-2 py-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              className="h-7 px-2 text-muted-foreground hover:text-destructive"
            >
              {isDeleting ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
